
import { useState } from 'react';
import { ExternalLink, Image as ImageIcon } from 'lucide-react';

interface MediaDisplayProps {
  media?: {
    type: string;
    url: string;
    thumbnailUrl?: string;
  };
  platform: string;
  videoId?: string;
}

const MediaDisplay = ({ media, platform, videoId }: MediaDisplayProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (!media || !media.url || hasError) {
    // Fallback display for YouTube videos if we have a videoId
    if (platform === 'youtube' && videoId) {
      return (
        <div className="relative overflow-hidden w-full h-0 pb-[42%] rounded-lg bg-gray-100 dark:bg-gray-800/50 -mt-6">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            className="absolute top-0 left-0 w-full h-full"
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    }
    
    // Generic fallback
    return (
      <div className="flex items-center justify-center h-24 bg-gray-100 dark:bg-gray-800/50 rounded-lg -mt-6">
        <div className="text-center">
          <ImageIcon className="h-8 w-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Média non disponible</p>
        </div>
      </div>
    );
  }

  if (media.type === 'image') {
    return (
      <div className="relative overflow-hidden rounded-lg -mt-6 max-h-80">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800/50">
            <div className="w-6 h-6 border-2 border-t-custom-primary dark:border-t-custom-secondaryLight border-gray-300 dark:border-gray-700 rounded-full animate-spin"></div>
          </div>
        )}
        <img
          src={media.url}
          alt="Media content"
          className={`w-full rounded-lg object-contain max-h-80 ${isLoaded ? '' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      </div>
    );
  }

  if (media.type === 'video') {
    return (
      <div className="relative overflow-hidden w-full h-0 pb-[42%] rounded-lg bg-gray-100 dark:bg-gray-800/50 -mt-6">
        <video
          src={media.url}
          className="absolute top-0 left-0 w-full h-full"
          controls
          poster={media.thumbnailUrl}
          onError={() => setHasError(true)}
        ></video>
      </div>
    );
  }

  // For YouTube videos
  if (media.type === 'youtube' && videoId) {
    return (
      <div className="relative overflow-hidden w-full h-0 pb-[42%] rounded-lg bg-gray-100 dark:bg-gray-800/50 -mt-6">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          className="absolute top-0 left-0 w-full h-full"
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  }

  // Fallback for unsupported media types
  return (
    <div className="flex items-center justify-center h-16 bg-gray-100 dark:bg-gray-800/50 rounded-lg -mt-6">
      <a
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-custom-primary dark:text-custom-secondaryLight hover:underline"
      >
        <span className="mr-1">Voir le contenu</span>
        <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
};

export default MediaDisplay;
